import { useEffect, useMemo, useState } from 'react';
import { marked } from 'marked';
import { Dialog, DialogBody, DialogTitle } from '../catalyst/dialog';
import { Button } from '../catalyst/button';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { formatDateLong } from '../lib/dateFormat';

const DEFAULT_VISIBLE = 5;
const LS_KEY = 'changelog-last-seen';

type ChangelogEntry = {
  date: string;
  items: string[];
};

function parseEntries(raw: string): ChangelogEntry[] {
  return raw
    .split(/^(?=# \d{4}-\d{2}-\d{2})/m)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((block) => {
      const [heading, ...rest] = block.split('\n');
      const date = heading.replace(/^#\s*/, '').trim();
      const items = rest.map((line) => line.replace(/^-\s*/, '').trim()).filter(Boolean);
      return { date, items };
    });
}

function formatDate(dateStr: string): string {
  return formatDateLong(new Date(dateStr + 'T00:00:00'));
}

function relativeLabel(dateStr: string): string | null {
  const now = new Date();
  const date = new Date(dateStr + 'T00:00:00');
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 7) return `${diffDays} days ago`;
  return null;
}

function splitBlocks(raw: string): string[] {
  return raw
    .split(/^(?=# \d{4}-\d{2}-\d{2})/m)
    .map((s) => s.trim())
    .filter(Boolean);
}

// The changelog ships as a standalone dist asset (emitted by
// vite-plugin-changelog) so changelog-only edits don't change the JS bundle
// and trigger the reload prompt. Fetched once per page load.
let changelogPromise: Promise<string> | null = null;
function loadChangelog(): Promise<string> {
  changelogPromise ??= fetch('/changelog.md', { cache: 'no-cache' })
    .then((res) => (res.ok ? res.text() : ''))
    .catch(() => '');
  return changelogPromise;
}

function useChangelogRaw(): string {
  const [raw, setRaw] = useState('');

  useEffect(() => {
    let active = true;
    loadChangelog().then((value) => {
      if (active) {
        setRaw(value);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return raw;
}

export function useHasNewChangelog() {
  const raw = useChangelogRaw();
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem(LS_KEY));
  const latest = splitBlocks(raw)[0] ?? null;
  const hasNew = latest !== null && latest !== lastSeen;

  // Resolve through the (cached) fetch promise so marking works even when the
  // dialog is opened before /changelog.md has finished loading.
  const markSeen = () => {
    loadChangelog().then((value) => {
      const seen = splitBlocks(value)[0];
      if (seen) {
        localStorage.setItem(LS_KEY, seen);
        setLastSeen(seen);
      }
    });
  };

  return { hasNew, markSeen };
}

function ChangelogEntryCard({ entry, isLatest }: { entry: ChangelogEntry; isLatest: boolean }) {
  const relative = relativeLabel(entry.date);

  return (
    <div className="relative pl-6">
      {/* Timeline dot */}
      <div
        className={`absolute -left-[5px] top-1.5 size-2.5 rounded-full ring-2 ring-white dark:ring-zinc-900 ${
          isLatest ? 'bg-blue-500' : 'bg-zinc-300 dark:bg-zinc-600'
        }`}
      />

      <div className="pb-6">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{formatDate(entry.date)}</span>
          {relative && <span className="text-xs text-zinc-400 dark:text-zinc-500">{relative}</span>}
        </div>
        <ul className="space-y-1.5">
          {entry.items.map((item, j) => (
            <li key={j} className="flex gap-2 text-sm text-zinc-600 dark:text-zinc-300">
              <span className="mt-1.5 shrink-0 size-1 rounded-full bg-zinc-400 dark:bg-zinc-500" />
              <span
                className="[&_code]:rounded [&_code]:bg-zinc-100 [&_code]:dark:bg-zinc-800 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs [&_code]:text-zinc-800 [&_code]:dark:text-zinc-200 [&_strong]:font-semibold [&_strong]:text-zinc-800 [&_strong]:dark:text-zinc-200 [&_em]:italic [&_a]:text-blue-600 [&_a]:hover:underline [&_a]:dark:text-blue-400"
                dangerouslySetInnerHTML={{ __html: marked.parseInline(item) as string }}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function ChangelogDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const raw = useChangelogRaw();
  const entries = useMemo(() => parseEntries(raw), [raw]);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? entries : entries.slice(0, DEFAULT_VISIBLE);
  const hasMore = entries.length > DEFAULT_VISIBLE;

  return (
    <Dialog open={open} onClose={onClose} size="4xl">
      <DialogTitle className="flex justify-between items-center">
        <span className="flex items-center gap-2">
          Changelog
          <span className="text-xs font-normal text-zinc-400 dark:text-zinc-500">{entries.length} updates</span>
        </span>
        <Button outline onClick={onClose}>
          <XMarkIcon className="size-4" />
        </Button>
      </DialogTitle>
      <DialogBody>
        <div className="max-h-[80vh] overflow-y-auto pr-2">
          {/* Timeline line */}
          <div className="relative border-l border-zinc-200 dark:border-zinc-700 ml-3">
            {visible.map((entry, i) => (
              <ChangelogEntryCard key={entry.date} entry={entry} isLatest={i === 0} />
            ))}
          </div>
        </div>
        {hasMore && !showAll && (
          <div className="mt-4 text-center">
            <Button outline onClick={() => setShowAll(true)}>
              Show older updates ({entries.length - DEFAULT_VISIBLE} more)
            </Button>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}
